import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { ViolationReturn } from '../../clients/rust-api-client';
import { fineStatusLabel, formatMoney } from '../../utils/billingHelpers';
import { SeverityGlyph, StatusIcon } from './StatusIcon';
import {
  ViolationGroup,
  formatDay,
  isOverdue,
  normalizeSeverity,
  shortRef,
} from './violationMeta';

/**
 * Linear's list view (#603 Part 2 §2): rows grouped by status under a
 * header with a count, one dense line per violation — severity, reference,
 * status, title — with the secondary facts as chips on the right. No cell
 * borders; a hover fill. Groups collapse.
 *
 * Presentational only: grouping, filtering and navigation belong to the
 * caller. Rows are buttons, so the list is fully keyboard-operable.
 */
interface Props {
  groups: ViolationGroup[];
  onOpen: (violation: ViolationReturn) => void;
  /** Show the fine chip (hidden where fines are not the reader's business). */
  showFine?: boolean;
  emptyMessage?: string;
}

const Chip: React.FC<{ className?: string; children: React.ReactNode; title?: string }> = ({
  className,
  children,
  title,
}) => (
  <span
    title={title}
    className={clsx(
      'inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[12px] leading-4 text-fg-muted',
      className
    )}
  >
    {children}
  </span>
);

// Keyed on FineStatus; colours follow front-porch's billing/enumLabels fineStatus.
const fineDot = (status: string) =>
  ({
    unpaid: 'bg-red-400',
    partially_paid: 'bg-amber-400',
    paid: 'bg-lime-400',
    reversed: 'bg-zinc-400',
  })[status] ?? 'bg-zinc-400';

export const Row: React.FC<{
  violation: ViolationReturn;
  onOpen: (v: ViolationReturn) => void;
  showFine: boolean;
}> = ({ violation: v, onOpen, showFine }) => {
  const overdue = isOverdue(v);
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(v)}
        className="group flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] hover:bg-fg/[0.03] focus:outline-none focus-visible:bg-fg/5"
      >
        <SeverityGlyph severity={v.severity} />
        <span className="hidden w-[72px] shrink-0 font-mono text-[12px] text-fg-muted sm:inline">
          {shortRef(v.id)}
        </span>
        <StatusIcon status={v.status} />
        <span className="min-w-0 flex-1 truncate font-medium text-fg">
          {v.description}
        </span>
        <span className="hidden items-center gap-1.5 md:flex">
          <Chip>{v.violation_type}</Chip>
          {showFine && v.fine && (
            <Chip title={`Fine: ${fineStatusLabel(v.fine.status)}`}>
              <span className={clsx('h-1.5 w-1.5 rounded-full', fineDot(v.fine.status))} aria-hidden />
              {formatMoney(v.fine.amount)}
            </Chip>
          )}
        </span>
        {v.due_date && (
          <span
            className={clsx(
              'hidden w-16 shrink-0 text-right text-[12px] sm:inline',
              overdue ? 'text-red-400' : 'text-fg-muted'
            )}
            title={overdue ? 'Overdue' : 'Due'}
          >
            {formatDay(v.due_date)}
          </span>
        )}
        <span className="sr-only">
          {`Severity ${normalizeSeverity(v.severity)}${overdue ? ', overdue' : ''}`}
        </span>
      </button>
    </li>
  );
};

export const GroupedViolationList: React.FC<Props> = ({
  groups,
  onOpen,
  showFine = true,
  emptyMessage = 'No violations match these filters.',
}) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (groups.length === 0) {
    return (
      <p className="px-4 py-10 text-center text-[13px] text-fg-muted">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div>
      {groups.map((g) => {
        const isCollapsed = !!collapsed[g.status];
        const headingId = `violation-group-${g.status}`;
        return (
          <section key={g.status} aria-labelledby={headingId}>
            <h3 id={headingId}>
              <button
                type="button"
                onClick={() =>
                  setCollapsed((c) => ({ ...c, [g.status]: !isCollapsed }))
                }
                aria-expanded={!isCollapsed}
                className="flex w-full items-center gap-2 bg-fg/[0.025] px-4 py-1.5 text-left text-[13px] font-medium text-fg-secondary hover:bg-fg/5"
              >
                <ChevronDownIcon
                  className={clsx(
                    'h-3.5 w-3.5 text-zinc-400 transition-transform',
                    isCollapsed && '-rotate-90'
                  )}
                  aria-hidden
                />
                <StatusIcon status={g.status} />
                {g.label}
                <span className="font-normal text-fg-muted">
                  {g.violations.length}
                </span>
              </button>
            </h3>
            {!isCollapsed && (
              <ul className="divide-y divide-line/50">
                {g.violations.map((v) => (
                  <Row key={v.id} violation={v} onOpen={onOpen} showFine={showFine} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
};
